import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useState
} from 'react';
import { useOutletContext } from 'react-router-dom';
import { List, Skeleton, Tabs, Tooltip } from 'antd';
import classNames from 'classnames';
import { ReactComponent as CommentsSvg } from '@assets/icons/MediaViewer/comments.svg';
import { ReactComponent as TaskSvg } from '@assets/icons/MediaViewer/task.svg';
import { ReactComponent as FileInfoSvg } from '@assets/icons/MediaViewer/file-info.svg';
import { useAssetMVPermissions, useTypedSelector } from '@hooks';
import Details from '@pages/MediaViewer/Details';
import { MediaViewerOutletContextState } from '@layouts/types';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { AssetViewerStateProvider } from '@context/AssetViewerStateContext';
import { getBrowser, getOS } from '@helpers/withKeysCanvasHistoryChange';
// import { ReactComponent as ActivitySvg } from '@assets/icons/MediaViewer/activity.svg';
import CompareVersionsModalContainer from '@pages/MediaViewer/components/CompareVersionsModal/CompareVersionsModalContainer';
import FloatCommentEditor from '@pages/MediaViewer/components/FloatCommentEditor';
import FloatCommentItem from '@pages/MediaViewer/components/FloatCommentItem';
import TranscriptDrawerContainer from '@pages/MediaViewer/components/TranscriptDrawer';
import { useResponsive } from '@hooks/useResponsive';
import { LgWidth, MdWidth } from '@components/Responsive';
import SidebarPanelDismissHandler from '@pages/MediaViewer/components/SidebarPanelDismissHandler';
import { assetVersionStateSelector } from '@redux/selectors/tasks';

import './MediaViewer.less';

const FabricContextProvider = React.lazy(
  () => import('@context/FabricContext')
);
const AssetViewer = React.lazy(() => import('@pages/MediaViewer/AssetViewer'));
const Comments = React.lazy(() => import('@pages/MediaViewer/Comments'));
const Tasks = React.lazy(() => import('@pages/MediaViewer/Tasks'));
// const Activity = React.lazy(() => import('@pages/MediaViewer/Activity'));
const AssetsSlider = React.lazy(
  () => import('@pages/MediaViewer/components/AssetsSlider')
);

type MediaViewerProps = {
  commentMentions: boolean;
  isLoadingAsset?: boolean;
  isShowAssetsSlider?: boolean;
};

const SIDEBAR_MOBILE_PEEK_MIN_PX = 75;

const sidebarPanelSuspenseFallback = (
  <List
    className="media_viewer_list-skeleton"
    itemLayout="horizontal"
    style={{ padding: 16, width: '100%' }}
    bordered={false}
    split={false}
    dataSource={new Array(10).fill(undefined).map((_, i) => ({ key: i }))}
    renderItem={() => (
      <List.Item>
        <Skeleton.Input style={{ height: 88 }} active />
      </List.Item>
    )}
  />
);

function getKeysMediaViewerShortcuts() {
  const macos = {
    comments: 'Shift + Option + 1',
    tasks: 'Shift + Option + 2',
    // activity: 'Shift + Option + 3',
    fileInfo: 'Shift + Option + 3'
  };
  const windows = {
    comments: 'Shift + Alt + 1',
    tasks: 'Shift + Alt + 2',
    // activity: 'Shift + Alt + 3',
    fileInfo: 'Shift + Alt + 3'
  };
  const os = getOS();
  const browser = getBrowser();
  if (os === 'macos') {
    if (browser === 'safari') return macos;
    if (browser === 'chrome') return macos;
  }
  if (os === 'windows') return windows;
  return windows;
}

function MediaViewer({
  commentMentions,
  isShowAssetsSlider = true,
  isLoadingAsset = false
}: MediaViewerProps) {
  const {
    commentsList,
    commentsLastReadAt,
    markCommentsRead,
    isLoadingCommentsList,
    tasksLastReadAt,
    latestTaskCreatedAt,
    markTasksRead
  } = useOutletContext<MediaViewerOutletContextState>();
  const { isMdWidth, isLgWidth } = useResponsive();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isPanelPinned, setIsPanelPinned] = useState(false);
  const sidebarPeekRef = useRef(SIDEBAR_MOBILE_PEEK_MIN_PX);
  const sidebarDragRef = useRef<{ startY: number; startPeek: number } | null>(
    null
  );

  const canViewTasks = useAssetMVPermissions('listTasks');
  const canCreateTask = useAssetMVPermissions('convertCommentToTask');

  const canReadComment = useAssetMVPermissions('readComments');
  const canLeaveComment = useAssetMVPermissions('comment');
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const asset = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const [selectedCollapse, setSelectedCollapse] = useState<string | null>(null);

  const isShowFloatCommentForm =
    asset &&
    canLeaveComment &&
    !!selectedVersion &&
    (!selectedCollapse || isMdWidth);

  const isShowFloatCommentItem =
    asset && !!selectedVersion && (!selectedCollapse || isMdWidth);

  const latestCommentCreatedAt = useMemo(() => {
    return commentsList
      .filter((x) => !x.user?.me)
      .reduce<string | null>((latest, comment) => {
        if (!latest) return comment.createdAt;
        return new Date(comment.createdAt) > new Date(latest)
          ? comment.createdAt
          : latest;
      }, null);
  }, [commentsList]);

  const tasksCount = useTypedSelector((state) =>
    selectedVersion
      ? assetVersionStateSelector(state, { assetVersionId: selectedVersion.id })
          .count
      : 0
  );

  const hasUnreadComments = useMemo(() => {
    if (!latestCommentCreatedAt) return false;
    if (!commentsLastReadAt) return true;
    return new Date(commentsLastReadAt) < new Date(latestCommentCreatedAt);
  }, [commentsLastReadAt, latestCommentCreatedAt]);

  const hasUnreadTasks = useMemo(() => {
    if (!latestTaskCreatedAt) return false;
    if (!tasksLastReadAt) return true;
    return new Date(tasksLastReadAt) < new Date(latestTaskCreatedAt);
  }, [tasksLastReadAt, latestTaskCreatedAt]);

  const sidebarMenuItems = useMemo(() => {
    const keys = getKeysMediaViewerShortcuts();
    return [
      {
        key: 'comments',
        icon: <CommentsSvg />,
        hasUnreadComments: selectedCollapse !== 'comments' && hasUnreadComments,
        tooltip: (
          <>
            Comments Panel
            <span className="media-viewer-tooltip-label">{keys.comments}</span>
          </>
        )
      },
      ...(canViewTasks
        ? [
            {
              key: 'tasks',
              icon: <TaskSvg />,
              className: 'tour-create-task-from-comment-tasks',
              hasUnreadComments: selectedCollapse !== 'tasks' && hasUnreadTasks,
              tooltip: (
                <>
                  Tasks Panel
                  <span className="media-viewer-tooltip-label">
                    {keys.tasks}
                  </span>
                </>
              )
            }
          ]
        : []),
      // {
      //   key: 'activity',
      //   icon: <ActivitySvg />,
      //   tooltip: (
      //     <>
      //       Activity Panel
      //       <span className="media-viewer-tooltip-label">{keys.activity}</span>
      //     </>
      //   )
      // },
      {
        key: 'fileInfo',
        icon: <FileInfoSvg />,
        tooltip: (
          <>
            Details Panel
            <span className="media-viewer-tooltip-label">{keys.fileInfo}</span>
          </>
        )
      }
    ];
  }, [canViewTasks, selectedCollapse, hasUnreadComments, hasUnreadTasks]);

  const onToggleOpenSelectedCollapse = useCallback(
    (value: string | null) => {
      setSelectedCollapse((prevSelectedCollapse) => {
        if (prevSelectedCollapse === value) return null;
        return value;
      });
    },
    [setSelectedCollapse]
  );

  const onTogglePanelPin = useCallback(() => {
    setIsPanelPinned((prev) => !prev);
  }, []);

  useEffect(() => {
    const onKeyPress = (e: KeyboardEvent) => {
      const isShortcutModifier = e.shiftKey && e.altKey;
      const keyCode = e.code;
      if (!isShortcutModifier) return;
      if (keyCode === 'Digit1') {
        onToggleOpenSelectedCollapse('comments');
        return;
      }
      if (keyCode === 'Digit2' && canViewTasks) {
        onToggleOpenSelectedCollapse('tasks');
        return;
      }
      if (keyCode === 'Digit3') {
        onToggleOpenSelectedCollapse('fileInfo');
      }
    };

    document.addEventListener('keydown', onKeyPress);
    return () => {
      document.removeEventListener('keydown', onKeyPress);
    };
  }, [canViewTasks, onToggleOpenSelectedCollapse]);

  const isCommentsOpened = selectedCollapse === 'comments';
  const markCommentsReadRef = useRef(markCommentsRead);
  markCommentsReadRef.current = markCommentsRead;
  useEffect(() => {
    if (isCommentsOpened) markCommentsReadRef.current();
  }, [isCommentsOpened, hasUnreadComments]);

  const isTasksOpened = selectedCollapse === 'tasks';
  const markTasksReadRef = useRef(markTasksRead);
  markTasksReadRef.current = markTasksRead;
  useEffect(() => {
    if (isTasksOpened) markTasksReadRef.current();
  }, [isTasksOpened, hasUnreadTasks]);

  const applySidebarPeekPx = useCallback((px: number) => {
    const shell = sidebarRef.current;
    if (!shell) return;
    const maxH = shell.offsetHeight;
    if (maxH < SIDEBAR_MOBILE_PEEK_MIN_PX) return;
    const clamped = Math.min(
      maxH,
      Math.max(SIDEBAR_MOBILE_PEEK_MIN_PX, Math.round(px))
    );
    sidebarPeekRef.current = clamped;
    shell.style.setProperty('--media-viewer-sidebar-offset', `${clamped}px`);
  }, []);

  useEffect(() => {
    if (!isMdWidth && sidebarRef.current) {
      sidebarPeekRef.current = SIDEBAR_MOBILE_PEEK_MIN_PX;
      sidebarRef.current.style.removeProperty('--media-viewer-sidebar-offset');
    }
  }, [isMdWidth]);

  useLayoutEffect(() => {
    if (!isMdWidth || !sidebarRef.current) return;
    const el = sidebarRef.current;
    const clampPeek = () => {
      applySidebarPeekPx(sidebarPeekRef.current);
    };
    clampPeek();
    const ro = new ResizeObserver(clampPeek);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMdWidth, applySidebarPeekPx]);

  const onSidebarHandlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isMdWidth || !sidebarRef.current) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      sidebarDragRef.current = {
        startY: e.clientY,
        startPeek: sidebarPeekRef.current
      };
    },
    [isMdWidth]
  );

  const onSidebarHandlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = sidebarDragRef.current;
      if (!drag || !sidebarRef.current) return;
      const dy = drag.startY - e.clientY;
      applySidebarPeekPx(drag.startPeek + dy);
    },
    [applySidebarPeekPx]
  );

  const onSidebarHandlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      sidebarDragRef.current = null;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    },
    []
  );

  const loader = (
    <div className="media_viewer_container">
      <div className="media_viewer_content">
        <PlaybackPreparingLoader />
      </div>
      <div className="media_viewer_sidebar" />
    </div>
  );

  if (!selectedVersion || isLoadingAsset) return loader;
  const viewerStateKey = `${asset?.id ?? ''}-${selectedVersion?.id ?? ''}`;
  return (
    <Suspense fallback={loader}>
      <div
        className={classNames({
          media_viewer_container: true,
          'b-content-full': !canReadComment && !canLeaveComment,
          'b-task-hide': !canViewTasks
        })}
      >
        <AssetViewerStateProvider
          key={viewerStateKey}
          selectedVersion={selectedVersion}
          commentsList={commentsList ?? []}
        >
          <SidebarPanelDismissHandler
            sidebarRef={sidebarRef}
            isOpen={!!selectedCollapse}
            isPinned={isPanelPinned}
            isLgWidth={isLgWidth}
            onClose={() => onToggleOpenSelectedCollapse(null)}
          />
          <FabricContextProvider>
            <div className="media_viewer_content">
              <TranscriptDrawerContainer />
              {isShowAssetsSlider && <AssetsSlider />}
              <AssetViewer setSelectedCollapse={setSelectedCollapse} />
              {isShowFloatCommentForm && (
                <FloatCommentEditor
                  asset={asset}
                  canCreateTask={canCreateTask}
                  commentMentions={commentMentions}
                  selectedVersion={selectedVersion}
                />
              )}
              {isShowFloatCommentItem && (
                <FloatCommentItem
                  asset={asset}
                  selectedVersion={selectedVersion}
                  commentMentions={commentMentions}
                  canCreateTask={canCreateTask}
                />
              )}
            </div>
            {(canReadComment || canLeaveComment) && (
              <div ref={sidebarRef} className="media_viewer_sidebar">
                <LgWidth>
                  <div className="media_viewer_sidebar-menu">
                    {sidebarMenuItems.map((item) => (
                      <Tooltip
                        key={item.key}
                        title={
                          <div
                            className="media-viewer-tooltip-inner"
                            style={{ padding: 8 }}
                          >
                            {item.tooltip}
                          </div>
                        }
                        overlayInnerStyle={{ padding: 0 }}
                        placement="right"
                        overlayClassName="toggle_favorite_overlay"
                      >
                        <div className="media_viewer_sidebar-menu-item">
                          <div
                            onClick={() =>
                              onToggleOpenSelectedCollapse(item.key)
                            }
                            className={classNames(
                              'media_viewer_sidebar-menu-btn',
                              item.className,
                              {
                                active: item.key === selectedCollapse
                              }
                            )}
                          >
                            {item.icon}
                            {item.hasUnreadComments && (
                              <span className="media_viewer_sidebar-menu-badge" />
                            )}
                          </div>
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                  <div
                    className={classNames('media_viewer_sidebar-content', {
                      'b-open': !!selectedCollapse
                    })}
                  >
                    <Suspense fallback={sidebarPanelSuspenseFallback}>
                      {selectedCollapse === 'comments' &&
                        !!selectedVersion &&
                        !!asset && (
                          <Comments
                            canCreateTask={canCreateTask}
                            selectedVersion={selectedVersion}
                            commentsList={commentsList}
                            isLoadingCommentsList={isLoadingCommentsList}
                            asset={asset}
                            onClosePanel={() =>
                              onToggleOpenSelectedCollapse(null)
                            }
                            commentMentions={commentMentions}
                            isPanelPinned={isPanelPinned}
                            onTogglePanelPin={onTogglePanelPin}
                          />
                        )}

                      {selectedCollapse === 'tasks' &&
                        canViewTasks &&
                        !!selectedVersion &&
                        !!asset && (
                          <Tasks
                            onToggleOpenSelectedCollapse={
                              onToggleOpenSelectedCollapse
                            }
                            canCreateTask={canCreateTask}
                            selectedVersion={selectedVersion}
                            asset={asset}
                            isPanelPinned={isPanelPinned}
                            onTogglePanelPin={onTogglePanelPin}
                          />
                        )}
                      {/* {selectedCollapse === 'activity' && (
                      <Activity
                        onClosePanel={() => onToggleOpenSelectedCollapse(null)}
                      />
                    )} */}
                      {selectedCollapse === 'fileInfo' && (
                        <Details
                          onClosePanel={() =>
                            onToggleOpenSelectedCollapse(null)
                          }
                          isPanelPinned={isPanelPinned}
                          onTogglePanelPin={onTogglePanelPin}
                        />
                      )}
                    </Suspense>
                  </div>
                </LgWidth>
                <MdWidth>
                  <div
                    className="media_viewer_sidebar-bottom-sheet"
                    onPointerDown={onSidebarHandlePointerDown}
                    onPointerMove={onSidebarHandlePointerMove}
                    onPointerUp={onSidebarHandlePointerUp}
                    onPointerCancel={onSidebarHandlePointerUp}
                  >
                    <div className="media_viewer_sidebar-bottom-sheet-line" />
                  </div>
                  <Tabs
                    activeKey={selectedCollapse || 'comments'}
                    onChange={(key) => setSelectedCollapse(key)}
                  >
                    <Tabs.TabPane
                      tab={
                        <>
                          Comment{' '}
                          {commentsList?.length ? (
                            <span>{commentsList?.length}</span>
                          ) : null}
                        </>
                      }
                      key="comments"
                    >
                      <Suspense fallback={sidebarPanelSuspenseFallback}>
                        {!!selectedVersion && !!asset && (
                          <Comments
                            canCreateTask={canCreateTask}
                            selectedVersion={selectedVersion}
                            commentsList={commentsList}
                            isLoadingCommentsList={isLoadingCommentsList}
                            asset={asset}
                            onClosePanel={() =>
                              onToggleOpenSelectedCollapse(null)
                            }
                            commentMentions={commentMentions}
                          />
                        )}
                      </Suspense>
                    </Tabs.TabPane>

                    {canViewTasks && (
                      <Tabs.TabPane
                        tab={
                          <>
                            Tasks{' '}
                            {tasksCount ? <span>{tasksCount}</span> : null}
                          </>
                        }
                        key="tasks"
                      >
                        <Suspense fallback={sidebarPanelSuspenseFallback}>
                          {canViewTasks && !!selectedVersion && !!asset && (
                            <Tasks
                              onToggleOpenSelectedCollapse={
                                onToggleOpenSelectedCollapse
                              }
                              canCreateTask={canCreateTask}
                              selectedVersion={selectedVersion}
                              asset={asset}
                            />
                          )}
                        </Suspense>
                      </Tabs.TabPane>
                    )}
                    <Tabs.TabPane tab="Details" key="details">
                      <Suspense fallback={sidebarPanelSuspenseFallback}>
                        <Details />
                      </Suspense>
                    </Tabs.TabPane>
                  </Tabs>
                </MdWidth>
              </div>
            )}
          </FabricContextProvider>
        </AssetViewerStateProvider>
        <CompareVersionsModalContainer />
      </div>
    </Suspense>
  );
}

export default MediaViewer;
